import { format } from 'date-fns'
import { CreditCard } from 'lucide-react'

import {
	Order,
	OrderRefund,
	OrderStatus,
	OrderTransaction,
	TransactionStatus,
	TransactionStatusType,
	GiftCardRedemption
} from '@/api/admin-orders-types'
import { OrderPaymentStatus, OrderPaymentStatusType } from '@/api/common-types'
import { Container, ContainerHeader } from '@/components/base/container'
import { StatusBadge } from '@/components/base/status-badge'
import { Text } from '@/components/base/text'
import { Tooltip } from '@/components/base/tooltip'
import { useI18n } from '@/i18n/i18n-react'
import { ArrowDownRightMini } from '@/icons/arrow-down-right-mini'
import { DocumentText } from '@/icons/document-text'
import { XCircle } from '@/icons/x-circle'
import { largerThan } from '@/utils/currency'

const getTransactionStatusColor = (status: TransactionStatusType) => {
	switch (status) {
		case TransactionStatus.Failed:
			return 'red' as const
		case TransactionStatus.Success:
			return 'green'
		default:
			return 'orange'
	}
}

const getOrderPaymentStatusColor = (status: OrderPaymentStatusType) => {
	switch (status) {
		case OrderPaymentStatus.PartiallyPaid:
		case OrderPaymentStatus.PartiallyRefunded:
			return 'orange'
		case OrderPaymentStatus.Refunded:
		case OrderPaymentStatus.Overpaid:
			return 'red'
		default:
			return 'green'
	}
}

export const OrderPaymentsSection = ({ order }: { order: Order }) => {
	const { L } = useI18n()

	const canRefund =
		order.type !== 'EXTERNAL' &&
		order.status !== OrderStatus.Canceled &&
		largerThan(order.refundableAmount ?? { value: 0, precision: 2 }, 0)

	return (
		<Container className='divide-y divide-dashed'>
			<ContainerHeader
				heading={L.OrderDetail.Payment.Title()}
				rightSide={
					<StatusBadge color={getOrderPaymentStatusColor(order.paymentStatus)} className='text-nowrap'>
						{L.Enum.PaymentStatus(order.paymentStatus)}
					</StatusBadge>
				}
				actions={[
					{
						actions: [
							{
								label: L.OrderDetail.Payment.Refund(),
								icon: <XCircle />,
								to: `/orders/${order.uuid}/refund`,
								disabled: !canRefund
							}
						]
					}
				]}
			/>

			<div className='flex flex-col divide-y divide-dashed'>
				{order.transactions.map(transaction => (
					<Payment key={transaction.uuid} transaction={transaction} currencyCode={order.currencyCode} />
				))}
			</div>
			<Total order={order} />
		</Container>
	)
}

const Refund = ({ refund, currencyCode }: { refund: OrderRefund; currencyCode: string }) => {
	const { L } = useI18n()
	const isPending = refund.status === TransactionStatus.Pending

	return (
		<div className='grid grid-cols-[1fr_1fr_1fr_20px] items-center gap-x-4 bg-ui-bg-subtle px-3 py-2 text-ui-fg-subtle sm:px-6 sm:py-4'>
			<div className='flex flex-row'>
				<div className='self-center pr-3'>
					<ArrowDownRightMini className='text-ui-fg-muted' />
				</div>
				<div>
					<Text size='small' leading='compact' weight='plus'>
						{refund.sequenceNumber
							? L.OrderDetail.Payment.RefundWithSequenceNumber({
									sequenceNumber: refund.sequenceNumber
								})
							: L.OrderDetail.Payment.Refund()}{' '}
						{refund.description && (
							<Tooltip content={refund.description}>
								<DocumentText className='ml-1 inline text-ui-tag-neutral-icon' />
							</Tooltip>
						)}
					</Text>
					<Text size='small' leading='compact'>
						{format(new Date(refund.date), 'dd MMM, yyyy, HH:mm:ss')}
					</Text>
				</div>
			</div>
			<div className='flex items-center justify-end'>
				{isPending && (
					<StatusBadge color='orange' className='text-nowrap'>
						{L.Enum.TransactionStatus(refund.status)}
					</StatusBadge>
				)}
			</div>
			<div className='flex items-center justify-end'>
				<Text size='small' leading='compact'>
					{L.Amount.Default({
						amount: refund.amount,
						currencyCode
					})}
				</Text>
			</div>
		</div>
	)
}

const GiftCard = ({
	order,
	redemption,
	currencyCode
}: {
	order: Order
	redemption: GiftCardRedemption
	currencyCode: string
}) => {
	const { L } = useI18n()

	return (
		<div className='divide-y divide-dashed'>
			<div className='grid grid-cols-[1fr_1fr_1fr_20px] items-center gap-x-4 px-3 py-2 text-ui-fg-subtle sm:grid-cols-[1fr_1fr_1fr_1fr_20px] sm:px-6 sm:py-4'>
				<div className='w-full min-w-[60px] overflow-hidden'>
					<Text size='small' leading='compact' weight='plus' className='truncate'>
						{redemption.name}
					</Text>
				</div>
				<div className='flex items-center justify-end'>
					<StatusBadge color={'green'} className='text-nowrap'>
						{L.Enum.TransactionStatus(TransactionStatus.Success)}
					</StatusBadge>
				</div>
				<div className='flex items-center justify-end'>
					<Text size='small' leading='compact'>
						{L.Amount.Default({
							amount: redemption.amount,
							currencyCode
						})}
					</Text>
				</div>
			</div>
		</div>
	)
}

const Payment = ({ transaction, currencyCode }: { transaction: OrderTransaction; currencyCode: string }) => {
	const { L } = useI18n()

	return (
		<div className='divide-y divide-dashed'>
			<div className='grid grid-cols-[1fr_1fr_1fr_20px] items-center gap-x-4 px-3 py-2 text-ui-fg-subtle sm:grid-cols-[1fr_1fr_1fr_1fr_20px] sm:px-6 sm:py-4'>
				<div className='w-full min-w-[60px] overflow-hidden'>
					<Text size='small' leading='compact' weight='plus' className='truncate'>
						{L.OrderDetail.Payment.PaymentWithSequenceNumber({
							sequenceNumber: transaction.sequenceNumber
						})}
					</Text>
					<Text size='small' leading='compact'>
						{format(new Date(transaction.date), 'dd MMM, yyyy, HH:mm:ss')}
					</Text>
					{transaction.lastDigits && (
						<div className='mt-1 flex items-center gap-x-1.5'>
							<CreditCard className='h-3.5 w-3.5 text-ui-fg-muted' />
							<Text size='small' leading='compact' className='text-ui-fg-muted'>
								•••• {transaction.lastDigits}
								{transaction.cardIssuerName && ` · ${transaction.cardIssuerName}`}
							</Text>
						</div>
					)}
				</div>
				<div className='hidden items-center justify-end sm:flex'>
					<Text size='small' leading='compact' className='capitalize'>
						{L.Enums.PaymentProvider(transaction.provider)}
					</Text>
				</div>
				<div className='flex items-center justify-end'>
					<StatusBadge color={getTransactionStatusColor(transaction.status)} className='text-nowrap'>
						{L.Enum.TransactionStatus(transaction.status)}
					</StatusBadge>
				</div>
				<div className='flex items-center justify-end'>
					<Text size='small' leading='compact'>
						{L.Amount.Default({
							amount: transaction.amount,
							currencyCode
						})}
					</Text>
				</div>
			</div>
			{transaction.refunds.map(refund => (
				<Refund key={refund.uuid} refund={refund} currencyCode={currencyCode} />
			))}
		</div>
	)
}

const Total = ({ order }: { order: Order }) => {
	const { L } = useI18n()

	return (
		<div>
			<div className='flex items-center justify-between px-3 py-2 sm:px-6 sm:py-4'>
				<Text size='small' weight='plus' leading='compact'>
					{L.OrderDetail.Payment.TotalPaidByCustomer()}
				</Text>

				<Text size='small' weight='plus' leading='compact'>
					{L.Amount.Default({
						amount: order.transactionNetAmount,
						currencyCode: order.currencyCode
					})}
				</Text>
			</div>

			{order.status !== OrderStatus.Canceled && largerThan(order.outstandingAmount, 0) && (
				<div className='flex items-center justify-between px-3 py-2 sm:px-6 sm:py-4'>
					<Text size='small' weight='plus' leading='compact'>
						{L.OrderDetail.Payment.TotalPending()}
					</Text>

					<Text size='small' weight='plus' leading='compact'>
						{L.Amount.Default({
							amount: order.outstandingAmount,
							currencyCode: order.currencyCode
						})}
					</Text>
				</div>
			)}
		</div>
	)
}
